// src/hooks/useBoundaryDetection.js
import { useCallback } from 'react'
import { useCityBoundaries } from './useCityBoundaries'
import { isPointInCityBounds, detectCityFromCoordinates } from '../utils/cityDetector'

/**
 * Hook que proporciona funciones para verificar si puntos están dentro de los límites
 * @returns {Object} Funciones de verificación
 */
export const useBoundaryDetection = () => {
  const { currentCity } = useCityBoundaries()

  /**
   * Verifica si un punto está dentro de la ciudad actual
   * @param {number[]} coordinates - [lng, lat]
   * @returns {Promise<boolean>}
   */
  const isPointInCurrentCity = useCallback(
    async coordinates => {
      if (!currentCity) return true // Si no hay ciudad detectada, permitir todo
      return await isPointInCityBounds(coordinates, currentCity)
    },
    [currentCity],
  )

  /**
   * Verifica si un árbol (feature de GeoJSON) está dentro de la ciudad actual
   * @param {Object} treeFeature - Feature de GeoJSON con geometry.coordinates
   * @returns {Promise<boolean>}
   */
  const isTreeInCurrentCity = useCallback(
    async treeFeature => {
      if (!treeFeature || !treeFeature.geometry || !treeFeature.geometry.coordinates) {
        return false
      }
      return await isPointInCurrentCity(treeFeature.geometry.coordinates)
    },
    [isPointInCurrentCity],
  )

  /**
   * Filtra un array de árboles para obtener solo los de la ciudad actual
   * @param {Array} trees - Array de features de GeoJSON
   * @returns {Promise<Array>} Árboles filtrados
   */
  const filterTreesByCurrentCity = useCallback(
    async trees => {
      if (!currentCity || !trees) return trees

      // Procesa todas las validaciones en paralelo
      const results = await Promise.all(
        trees.map(async tree => ({
          tree,
          isValid: await isTreeInCurrentCity(tree),
        })),
      )

      return results.filter(({ isValid }) => isValid).map(({ tree }) => tree)
    },
    [currentCity, isTreeInCurrentCity],
  )

  /**
   * Detecta a qué ciudad pertenece un punto específico
   * @param {number[]} coordinates - [lng, lat]
   * @returns {Promise<'loja' | 'cuenca' | null>}
   */
  const detectCity = useCallback(async coordinates => {
    return await detectCityFromCoordinates(coordinates)
  }, [])

  /**
   * Valida si una coordenada es válida para ser agregada en la ciudad actual
   * Útil para el modo de captura de ubicación
   * @param {number[]} coordinates - [lng, lat]
   * @returns {Promise<Object>} { isValid: boolean, city: string | null, message: string }
   */
  const validateCoordinatesForCity = useCallback(
    async coordinates => {
      if (!coordinates || coordinates.length !== 2) {
        return {
          isValid: false,
          city: null,
          message: 'Coordenadas inválidas',
        }
      }

      if (!currentCity) {
        return {
          isValid: true,
          city: null,
          message: 'Sin restricción de ciudad',
        }
      }

      const isValid = await isPointInCurrentCity(coordinates)
      const detectedCity = await detectCity(coordinates)

      if (isValid) {
        return {
          isValid: true,
          city: currentCity,
          message: `Punto válido para ${currentCity}`,
        }
      } else {
        return {
          isValid: false,
          city: detectedCity,
          message: detectedCity
            ? `Este punto pertenece a ${detectedCity}, pero estás en ${currentCity}`
            : `Este punto está fuera de los límites`,
        }
      }
    },
    [currentCity, isPointInCurrentCity, detectCity],
  )

  return {
    currentCity,
    isPointInCurrentCity,
    isTreeInCurrentCity,
    filterTreesByCurrentCity,
    detectCity,
    validateCoordinatesForCity,
  }
}

/**
 * Hook que filtra automáticamente los datos del mapa según la ciudad actual
 * Útil para aplicar filtros geográficos a los árboles mostrados
 * @param {Object} geojsonData - GeoJSON FeatureCollection con todos los árboles
 * @returns {Object} GeoJSON filtrado por ciudad (se calcula de forma asíncrona)
 */
export const useFilterTreesByCity = geojsonData => {
  const { currentCity } = useBoundaryDetection()

  // IMPORTANTE: Este hook retorna los datos sin filtrar inmediatamente
  // Para usarlo correctamente, debes llamar a filterTreesByCurrentCity manualmente
  // en un useEffect o evento asíncrono

  if (!geojsonData || !geojsonData.features) {
    return geojsonData
  }

  // Si no hay ciudad detectada, retornar todos los datos
  if (!currentCity) {
    return geojsonData
  }

  // NOTA: No podemos hacer await aquí porque los hooks no pueden ser async
  // El componente que use este hook debe manejar el filtrado de forma asíncrona
  console.warn('⚠️ useFilterTreesByCity requiere filtrado asíncrono manual')

  return geojsonData
}
